import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const ReserveDateJsonLd = () => (
  <script type="application/ld+json">{JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/reserve-date#webpage`,
        "url": `${SITE_URL}/reserve-date`,
        "name": "Reserve a Date — E-Waste Event | Greenly Electronics Recycling",
        "description": "Reserve your e-waste collection event date with Greenly. Fill out our quick date inquiry form and we'll confirm your event within 1 business day.",
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "about": { "@id": `${SITE_URL}/#business` },
        "breadcrumb": { "@id": `${SITE_URL}/reserve-date#breadcrumb` }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${SITE_URL}/reserve-date#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "E-Waste Events", "item": `${SITE_URL}/events` },
          { "@type": "ListItem", "position": 3, "name": "Reserve a Date", "item": `${SITE_URL}/reserve-date` }
        ]
      },
      {
        "@type": "Service",
        "@id": `${SITE_URL}/reserve-date#service`,
        "name": "E-Waste Collection Event Booking — Los Angeles",
        "description": "Reserve a date for a community e-waste collection event in Los Angeles. Greenly provides the truck, crew, and certified recycling. Schools, businesses, churches, and nonprofits welcome.",
        "url": `${SITE_URL}/reserve-date`,
        "provider": { "@id": `${SITE_URL}/#business` },
        "areaServed": { "@type": "AdministrativeArea", "name": "Los Angeles County" },
        "serviceType": "E-Waste Collection Event",
        "offers": {
          "@type": "Offer",
          "price": "200",
          "priceCurrency": "USD",
          "description": "$200/hr · 3-hour minimum ($600) · Includes truck, 2 techs, supplies, gas & insurance"
        }
      }
    ]
  })}</script>
);

const ReserveDatePage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

const FORM_URL = 'https://greenlyelectronicsrecycling.com/api/resend-readyai-form.php';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === 'string' && key !== 'website_url') data.append(key, value);
    });

    // Honeypot check — bots fill this, real users don't see it
    const honeypot = formData.get('website_url');
    if (honeypot && (honeypot as string).trim() !== '') {
      setSubmitted(true);
      form.reset();
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setSubmitted(true);
      }
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <title>Reserve a Date — E-Waste Event | Greenly Electronics Recycling</title>
      <meta name="description" content="Reserve your e-waste collection event date with Greenly in Los Angeles. Quick inquiry form — we confirm within 1 business day. Schools, businesses & nonprofits welcome." />
      <meta name="keywords" content="reserve e-waste event Los Angeles, schedule recycling event LA, book electronics recycling event" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/reserve-date`} />
      <ReserveDateJsonLd />
      <Navbar />
      <main className="min-h-screen pt-16" style={{ background: '#d6e8dc' }}>

        {/* Top hero bar */}
        <div className="bg-[#1a4a2e] py-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 border border-white/25 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <i className="ri-calendar-check-line"></i>
              Date Inquiry
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              Reserve Your Event Date
            </h1>
            <p className="text-white/70 text-sm leading-relaxed">
              Tell us when works for you and we&apos;ll confirm availability within 1 business day.
            </p>
          </div>
        </div>

        {/* Form card */}
        <div className="max-w-2xl mx-auto px-4 py-10">
          {submitted ? (
            <div className="bg-white rounded-2xl p-10 text-center border border-[#b0cdb8]">
              <div className="w-16 h-16 flex items-center justify-center bg-[#1B6B3A] rounded-full mx-auto mb-5">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Date Request Received!</h2>
              <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                We&apos;ll reach out within 1 business day to confirm your date and walk you through the next steps.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:+12136001000"
                  className="inline-flex items-center justify-center gap-2 bg-[#1B6B3A] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-line"></i>
                  Call (213) 600-1000
                </a>
                <Link
                  to="/events"
                  className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 font-semibold px-6 py-3 rounded-lg hover:border-[#1B6B3A] hover:text-[#1B6B3A] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Events
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#b0cdb8]">
              <form id="reserve-date-form" onSubmit={handleSubmit} className="space-y-5">
                <input type="hidden" name="page" value="reserve-date" />
                {/* Honeypot field — hidden from real users, bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <label htmlFor="website_url_hp_reserve">Website URL</label>
                  <input
                    id="website_url_hp_reserve"
                    type="text"
                    name="website_url"
                    defaultValue=""
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Name + Org */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">Your Name *</label>
                    <input
                      type="text"
                      name="contact_name"
                      required
                      placeholder="Full name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">Organization *</label>
                    <input
                      type="text"
                      name="organization_name"
                      required
                      placeholder="School, business, church..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(213) 000-0000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Preferred Date + Backup Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">Preferred Date *</label>
                    <input
                      type="date"
                      name="preferred_date"
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-800 mb-1.5">
                      Backup Date <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="date"
                      name="backup_date"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Event Address */}
                <div>
                  <label className="block text-xs font-bold text-gray-800 mb-1.5">Event Location / Address *</label>
                  <input
                    type="text"
                    name="event_address"
                    required
                    placeholder="Street address, city, zip"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-bold text-gray-800 mb-1.5">
                    Additional Notes <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    maxLength={500}
                    placeholder="Any special requirements, data destruction needs, parking info..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1B6B3A] bg-[#f0f7f3] text-gray-800 placeholder-gray-400 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold py-3.5 rounded-lg transition-colors cursor-pointer text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-check-line"></i>
                  {submitting ? 'Submitting...' : 'Submit Date Request'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Prefer to call?{' '}
                  <a href="tel:+12136001000" className="text-[#1B6B3A] font-semibold hover:underline cursor-pointer">
                    (213) 600-1000
                  </a>
                </p>
              </form>
            </div>
          )}

          {/* Back link */}
          <div className="text-center mt-6">
            <Link to="/events" className="inline-flex items-center gap-1.5 text-[#1a4a2e] text-sm font-semibold hover:underline cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Events page
            </Link>
          </div>
        </div>

      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default ReserveDatePage;
